import { FunctionComponent, useState, useMemo } from "react"
import { useTypedSelector } from "../../app/store"
import {
  useGetMeasuresQuery,
  useGetOrganizationMeasuresQuery,
  useCreateOrganizationMeasureMutation,
} from "../../app/services/meseeknaApiV2"

type SelectedMeasure = {
  id: number
  name: string
  description: string
  isPurchased: boolean
  apiMeasureId?: number // The actual ID from the API/database
}

// The 30 behavioral competencies - names must match database exactly (from seedMeasures.ts)
const BEHAVIORAL_MEASURES = [
  { id: 1, name: "Advanced Strategy", definition: "Using strategic thinking to make complex decisions with long-term impact." },
  { id: 2, name: "Breadth of Approach", definition: "Considering multiple perspectives and approaches to solve problems comprehensively." },
  { id: 3, name: "Collaboration", definition: "Working effectively with others to achieve shared goals and outcomes." },
  { id: 4, name: "Communication", definition: "Expressing ideas clearly and effectively, making sure messages are understood by others." },
  { id: 5, name: "Conflict Approach", definition: "Managing and approaching conflicts constructively and professionally." },
  { id: 6, name: "Conflict Response", definition: "Responding to conflicts in ways that promote resolution and understanding." },
  { id: 7, name: "Conflict Resolution", definition: "Resolving disputes and disagreements through effective mediation and problem-solving." },
  { id: 8, name: "Creative Decisiveness", definition: "Taking action with confidence while thinking outside the box to solve problems." },
  { id: 9, name: "Creative Flexibility", definition: "Adapting creatively to changing circumstances and finding innovative solutions." },
  { id: 10, name: "Crisis Preparedness", definition: "Preparing for and anticipating potential crises before they occur." },
  { id: 11, name: "Crisis Response", definition: "Responding effectively and calmly during crisis situations." },
  { id: 12, name: "Crisis Recovery", definition: "Leading recovery efforts and learning from crisis experiences." },
  { id: 13, name: "Dependability", definition: "Being reliable and following through on commitments to yourself and others." },
  { id: 14, name: "Developmental Orientation", definition: "Always being eager to learn and grow, while helping others do the same." },
  { id: 15, name: "Emotional Resilience", definition: "Maintaining emotional stability and bouncing back from setbacks effectively." },
  { id: 16, name: "Empathetic Communication", definition: "Communicating with sensitivity and understanding of others' emotions and perspectives." },
  { id: 17, name: "Goal Management", definition: "Setting, tracking, and achieving goals systematically and effectively." },
  { id: 18, name: "Goal Orientation", definition: "Maintaining focus on objectives and working persistently toward achievement." },
  { id: 19, name: "Information Management", definition: "Organizing, processing, and utilizing information effectively for decision-making." },
  { id: 20, name: "Initiative", definition: "Taking proactive steps and self-directed action to achieve goals." },
  { id: 21, name: "Innovation", definition: "Finding creative and lasting solutions by working with others and thinking differently." },
  { id: 22, name: "People-Centrism", definition: "Putting people first and considering human impact in all decisions and actions." },
  { id: 23, name: "Proactivity", definition: "Taking initiative and acting in advance to prevent problems or create opportunities." },
  { id: 24, name: "Productivity", definition: "Achieving maximum output and efficiency in work and personal activities." },
  { id: 25, name: "Resource Management", definition: "Effectively utilizing available resources to achieve optimal outcomes." },
  { id: 26, name: "Strategic Approach", definition: "Using all available resources and opportunities in the best possible way to achieve goals." },
  { id: 27, name: "Strategic Quantitative Reasoning", definition: "Using data and quantitative analysis to inform strategic decisions." },
  { id: 28, name: "Team Orientation", definition: "Working collaboratively and prioritizing team success over individual achievement." },
  { id: 29, name: "Task Management", definition: "Organizing and handling work in ways that help achieve future goals." },
  { id: 30, name: "Workplace Engagement", definition: "Maintaining high levels of involvement, enthusiasm, and commitment at work." }
]

// Create a map of measure names for quick lookup
const MEASURE_NAMES = new Set(BEHAVIORAL_MEASURES.map(m => m.name.toLowerCase()))

const MeasuresLibrarySection: FunctionComponent = () => {
  const organization = useTypedSelector((state) => state.authSlice.organization)
  const [selectedMeasure, setSelectedMeasure] = useState<SelectedMeasure | null>(null)
  const [isUnlocking, setIsUnlocking] = useState(false)
  const [unlockError, setUnlockError] = useState<string | null>(null)
  const [unlockSuccess, setUnlockSuccess] = useState(false)

  // Fetch all measures from API to get database IDs
  const { data: measuresData, isLoading: isMeasuresLoading } = useGetMeasuresQuery()

  // Fetch organization's purchased measures
  const { data: orgMeasuresData, isLoading: isOrgMeasuresLoading, refetch: refetchOrgMeasures } = useGetOrganizationMeasuresQuery(
    Number(organization?.id) || 0,
    { skip: !organization?.id }
  )

  // Mutation for unlocking measures
  const [createOrganizationMeasure] = useCreateOrganizationMeasureMutation()

  // Create a map from measure name to API measure for ID lookup
  const apiMeasuresByName = useMemo(() => {
    if (!measuresData?.data) return new Map<string, { id: number; measureName: string }>()
    const map = new Map<string, { id: number; measureName: string }>()
    measuresData.data.forEach(m => {
      // Only include measures that match our behavioral measures by name
      if (MEASURE_NAMES.has(m.measureName.toLowerCase())) {
        map.set(m.measureName.toLowerCase(), m)
      }
    })
    return map
  }, [measuresData])

  // Get set of purchased measure names (from organization measures)
  const purchasedMeasureNames = useMemo(() => {
    if (!orgMeasuresData?.data) return new Set<string>()
    return new Set(
      orgMeasuresData.data
        .filter((om) => om.isActive)
        .map((om) => om.measure?.measureName?.toLowerCase() || "")
        .filter(name => name)
    )
  }, [orgMeasuresData])

  // Separate our hardcoded measures into purchased and available
  const { purchasedMeasures, availableMeasures } = useMemo(() => {
    const purchased = BEHAVIORAL_MEASURES.filter(m =>
      purchasedMeasureNames.has(m.name.toLowerCase())
    )
    const available = BEHAVIORAL_MEASURES.filter(m =>
      !purchasedMeasureNames.has(m.name.toLowerCase())
    )
    return { purchasedMeasures: purchased, availableMeasures: available }
  }, [purchasedMeasureNames])

  const handleMeasureClick = (measure: typeof BEHAVIORAL_MEASURES[0], isPurchased: boolean) => {
    // Find the API measure ID by name
    const apiMeasure = apiMeasuresByName.get(measure.name.toLowerCase())

    setSelectedMeasure({
      id: measure.id,
      name: measure.name,
      description: measure.definition,
      isPurchased,
      apiMeasureId: apiMeasure?.id
    })
    setUnlockError(null)
    setUnlockSuccess(false)
  }

  const closeModal = () => {
    setSelectedMeasure(null)
    setUnlockError(null)
    setUnlockSuccess(false)
  }

  const handleUnlockMeasure = async () => {
    if (!selectedMeasure || !organization?.id) return

    // Check if we have the API measure ID
    if (!selectedMeasure.apiMeasureId) {
      setUnlockError("This measure is not yet available in the system. Please contact support.")
      return
    }

    setIsUnlocking(true)
    setUnlockError(null)

    try {
      await createOrganizationMeasure({
        organizationId: Number(organization.id),
        measureId: selectedMeasure.apiMeasureId,
        isActive: true,
      }).unwrap()

      setUnlockSuccess(true)
      // Refetch organization measures to update the lists
      refetchOrgMeasures()

      // Close modal after a brief delay to show success
      setTimeout(() => {
        closeModal()
      }, 1500)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to unlock measure. Please try again."
      setUnlockError(errorMessage)
    } finally {
      setIsUnlocking(false)
    }
  }

  const isLoading = isMeasuresLoading || isOrgMeasuresLoading

  if (isLoading) {
    return (
      <div className="min-h-full bg-gray-50 py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4d86ff]"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-gray-50 py-12 px-8">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900">Measure Library</h1>
          <p className="text-lg text-gray-500 mt-3">
            Behavioral competencies validated by 500+ peer-reviewed publications—from Task Management to Crisis Response to Strategic Thinking. Review the measures you&apos;ve selected to understand scoring methodology and interpretation. Or unlock additional dimensions to deepen your team&apos;s cognitive blueprint.
          </p>
        </div>

        {/* Purchased Measures */}
        <div className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Your Measures</h2>
          <p className="text-lg text-gray-500 mb-4">Click to review definitions, scoring methodology, and how to apply insights</p>
          {purchasedMeasures.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
              <p className="text-gray-500">No measures selected yet. Unlock measures below to start mapping your team&apos;s cognitive patterns.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {purchasedMeasures.map((measure) => (
                <button
                  key={measure.id}
                  onClick={() => handleMeasureClick(measure, true)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-left hover:shadow-md hover:border-[#4d86ff]/50 transition-all group"
                >
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-[#50d16b] rounded-full mr-3 flex-shrink-0" />
                    <span className="text-gray-900 font-medium group-hover:text-[#4d86ff] transition-colors">
                      {measure.name}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Available Measures */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Available Measures</h2>
          <p className="text-lg text-gray-500 mb-4">Expand your cognitive map with additional validated dimensions</p>
          {availableMeasures.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
              <p className="text-gray-500">All measures have been unlocked.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableMeasures.map((measure) => (
                <button
                  key={measure.id}
                  onClick={() => handleMeasureClick(measure, false)}
                  className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-left hover:bg-white hover:shadow-sm hover:border-[#4d86ff]/50 transition-all group"
                >
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-gray-300 rounded-full mr-3 flex-shrink-0" />
                    <span className="text-gray-600 group-hover:text-gray-900 transition-colors">
                      {measure.name}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Measure Detail Modal */}
      {selectedMeasure && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-3 h-3 ${selectedMeasure.isPurchased ? 'bg-[#50d16b]' : 'bg-gray-300'} rounded-full mr-3`} />
                <h3 className="text-xl font-semibold text-gray-900">{selectedMeasure.name}</h3>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {selectedMeasure.isPurchased ? (
              <>
                <p className="text-gray-600 mb-4">
                  {selectedMeasure.description}
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Scoring Range</h4>
                  <p className="text-gray-600 text-sm">1-5 scale measuring behavioral tendencies across different workplace scenarios.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Application</h4>
                  <p className="text-gray-600 text-sm">Use insights to guide development conversations, team composition decisions, and performance coaching.</p>
                </div>
              </>
            ) : (
              <>
                {unlockSuccess ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-[#50d16b]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-[#50d16b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-[#50d16b] font-medium">Measure unlocked successfully!</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-600 mb-4">
                      {selectedMeasure.description}
                    </p>
                    <div className="bg-[#4d86ff]/10 border border-[#4d86ff]/30 rounded-lg p-4 mb-4">
                      <h4 className="text-sm font-medium text-[#3d5669] mb-1">Pricing</h4>
                      <p className="text-[#3d5669] text-sm">$8/month per measure. Added to your subscription automatically.</p>
                    </div>
                    {unlockError && (
                      <div className="bg-[#ff5050]/10 border border-[#ff5050]/30 rounded-lg p-3 mb-4">
                        <p className="text-[#ff5050] text-sm">{unlockError}</p>
                      </div>
                    )}
                    <button
                      onClick={handleUnlockMeasure}
                      disabled={isUnlocking}
                      className="w-full px-4 py-2.5 bg-[#4d86ff] text-white text-sm font-medium rounded-lg hover:bg-[#3d76ef] transition-colors disabled:bg-[#4d86ff]/50 disabled:cursor-not-allowed"
                    >
                      {isUnlocking ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Unlocking...
                        </span>
                      ) : (
                        "Unlock Measure"
                      )}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MeasuresLibrarySection
